import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import SchedulePicker from "../../components/SchedulePicker";
import RoomPicker from "../../components/RoomPicker";
import TeachingGridView from "./TeachingGridView";
import SectionRoster from "./SectionRoster";
import SectionRosterModal from "./SectionRosterModal";
import { syLabel } from "../../utils/schoolYear";
import s from "./ClassOfferingsPanel.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Room name for an offering — prefers the Room entity (roomId), falls
 *  back to the legacy free-text `room` column on older rows. */
function roomName(o, roomById) {
  return (o.roomId != null && roomById[o.roomId]?.name) || o.room || "";
}

/** Grade levels in school order (Nursery → Grade 12). */
function byGradeOrder(a, b) {
  return (a.ordering ?? 0) - (b.ordering ?? 0) || (a.name || "").localeCompare(b.name || "");
}

/** <option>s for a Section dropdown, grouped under their grade level so a
 *  teacher covering several levels can tell "Grade 7 · Joy" from
 *  "Kinder 1 · Joy". Sections without a known grade go last. */
function SectionOptions({ sections, gradeLevels }) {
  const groups = gradeLevels.slice().sort(byGradeOrder)
    .map(gl => ({ gl, secs: sections.filter(x => String(x.gradeLevelId) === String(gl.id)) }))
    .filter(g => g.secs.length > 0);
  const known = new Set(groups.flatMap(g => g.secs.map(x => x.id)));
  const other = sections.filter(x => !known.has(x.id));
  const sortByName = list => list.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return (
    <>
      {groups.map(({ gl, secs }) => (
        <optgroup key={gl.id} label={gl.name}>
          {sortByName(secs).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </optgroup>
      ))}
      {other.length > 0 && (
        <optgroup label="No grade level">
          {sortByName(other).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </optgroup>
      )}
    </>
  );
}

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

/** Custom replacement for the generic "Class Offerings" tab in Academics.
 *  Solves three pain points:
 *   - one row per (teacher, subject, section) noise → grouped by teacher
 *   - tedious "+ New" form for each row → a batch modal that assigns many
 *     classes to one teacher in a single pass
 *   - no way to find things → search + SY/semester filters at the top. */
export default function ClassOfferingsPanel() {
  const navigate = useNavigate();
  const [schoolYears,  setSchoolYears]  = useState([]);
  const [semesters,    setSemesters]    = useState([]);
  const [sections,     setSections]     = useState([]);
  const [subjects,     setSubjects]     = useState([]);
  const [offerings,    setOfferings]    = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [gradeLevels,  setGradeLevels]  = useState([]);
  const [departments,  setDepartments]  = useState([]); // "Levels" — TEACHING-tagged departments
  // Students + enrollments power the section-mode roster panel inside the
  // GroupDetailModal — eagerly fetched so opening the drill-down feels
  // instant. Failures are non-fatal: an empty roster just renders an
  // empty state.
  const [students,     setStudents]     = useState([]);
  const [enrollments,  setEnrollments]  = useState([]);
  const [rooms,        setRooms]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState("");

  const [syId,         setSyId]         = useState("");
  const [semId,        setSemId]        = useState("");
  const [search,       setSearch]       = useState("");
  const [showNew,      setShowNew]      = useState(false);
  // Teacher to pre-select in the batch modal ("" = let the admin pick).
  const [newTeacherId, setNewTeacherId] = useState("");
  const [detailTeacherId, setDetailTeacherId] = useState(null);
  const [groupBy,      setGroupBy]      = useState("teacher"); // "teacher" | "subject" | "section"
  // Narrows the WHOLE panel (List and Grid both) to one school level at a
  // time — without it, Section/Grade Level grouping just dumps all 25
  // sections alphabetically with no way to tell Nursery from Grade Ten at
  // a glance. "" = All.
  const [levelId,      setLevelId]      = useState("");
  // Narrows further to one grade level (Nursery, Kinder 1, Grade 7…)
  // inside the selected Level. "" = All grades.
  const [gradeId,      setGradeId]      = useState("");
  const [showEmpty,   setShowEmpty]    = useState(true);      // show items with 0 assignments
  const [detailGroup,  setDetailGroup]  = useState(null);      // {mode, entity, offs} for subject/section drill-down
  // Two visual modes for the same data: the original card list (audit /
  // bulk-assign view) and the new Mon–Fri teaching grid (planning view).
  // The grid is the visual "where to put things"; the list is the
  // tabular "what's assigned." Both read from the same offerings array.
  const [viewMode,     setViewMode]     = useState("list");    // "list" | "grid"

  async function loadAll() {
    setLoading(true);
    try {
      const [sy, sem, sec, subj, off, emp, gl, stu, enr, dept, rm] = await Promise.all([
        fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
        // "Levels" — TEACHING-tagged departments (Pre-School / Elementary /
        // Junior High / Senior High). Powers the Level tabs below — same
        // endpoint the Academic Directory uses for the same purpose.
        fetch(`${API_BASE}/api/departments?staffType=TEACHING`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
      ]);
      setSchoolYears(sy); setSemesters(sem); setSections(sec);
      setSubjects(subj);  setOfferings(off); setEmployees(emp);
      setGradeLevels(gl); setStudents(stu);  setEnrollments(enr);
      setDepartments(dept); setRooms(Array.isArray(rm) ? rm : []);
      return { sy, sem };
    } catch (e) {
      setErr(e.message);
      return { sy: [], sem: [] };
    } finally { setLoading(false); }
  }

  useEffect(() => {
    let cancelled = false;
    loadAll().then(({ sy, sem }) => {
      if (cancelled) return;
      const activeSy = sy.find(y => y.active) || sy[0];
      if (activeSy) setSyId(String(activeSy.id));
      const inSy = sem.filter(x => !activeSy || String(x.schoolYearId) === String(activeSy.id));
      const activeSem = inSy.find(x => x.active) || inSy[0];
      if (activeSem) setSemId(String(activeSem.id));
    });
    return () => { cancelled = true; };
  }, []);

  // If the SY changes and the current semester no longer belongs to it, pick
  // a fresh one — avoids an inconsistent filter combo.
  useEffect(() => {
    if (!syId) return;
    const stillValid = semesters.some(x => String(x.id) === semId && String(x.schoolYearId) === String(syId));
    if (stillValid) return;
    const inSy = semesters.filter(x => String(x.schoolYearId) === String(syId));
    const fresh = inSy.find(x => x.active) || inSy[0];
    setSemId(fresh ? String(fresh.id) : "");
  }, [syId, semId, semesters]);

  // ── Derived lookups ─────────────────────────────────────────────────
  const subjectById    = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const sectionById    = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const employeeById   = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
  const semesterById   = useMemo(() => Object.fromEntries(semesters.map(x => [x.id, x])), [semesters]);
  const syById         = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const gradeLevelById = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);
  const roomById       = useMemo(() => Object.fromEntries(rooms.map(r => [r.id, r])), [rooms]);

  /** True when `sec` belongs to the currently-selected Level (or always
   *  true when no Level is picked — "All"). A section's level comes from
   *  its grade level's departmentId, same chain the Academic Directory
   *  uses. Missing/unresolvable grade data fails OPEN (shown, not
   *  hidden) so a data gap never makes a real section silently vanish
   *  from the panel. */
  function sectionInLevel(sec) {
    if (gradeId && sec && String(sec.gradeLevelId) !== String(gradeId)) return false;
    if (!levelId) return true;
    const gl = sec ? gradeLevelById[sec.gradeLevelId] : null;
    if (!gl) return true;
    return String(gl.departmentId) === String(levelId);
  }
  function gradeInLevel(gl) {
    if (gradeId && gl && String(gl.id) !== String(gradeId)) return false;
    if (!levelId) return true;
    if (!gl) return true;
    return String(gl.departmentId) === String(levelId);
  }

  /** Grade levels offered in the Grade Level dropdown — only those under
   *  the selected Level (all of them for "All"), in school order. */
  const gradeOptions = useMemo(() => {
    return gradeLevels
      .filter(gl => !levelId || String(gl.departmentId) === String(levelId))
      .slice()
      .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0) || (a.name || "").localeCompare(b.name || ""));
  }, [gradeLevels, levelId]);

  // Switching Level drops a grade that doesn't belong to the new one.
  useEffect(() => {
    if (gradeId && !gradeOptions.some(gl => String(gl.id) === String(gradeId))) setGradeId("");
  }, [gradeId, gradeOptions]);

  /** Section counts per Level, scoped by School Year only (not by the
   *  Level filter itself) — so every tab's own count stays stable no
   *  matter which tab is currently active, instead of the inactive
   *  tabs all reading 0 the moment you've already switched away from
   *  them. */
  const sectionCountByLevel = useMemo(() => {
    const map = new Map();
    let total = 0;
    for (const sec of sections) {
      if (syId && String(sec.schoolYearId) !== String(syId)) continue;
      total++;
      const gl = gradeLevelById[sec.gradeLevelId];
      if (gl?.departmentId != null) {
        map.set(gl.departmentId, (map.get(gl.departmentId) || 0) + 1);
      }
    }
    map.set("__all__", total);
    return map;
  }, [sections, syId, gradeLevelById]);

  // Some school setups have accumulated duplicate/legacy department rows
  // that were all tagged TEACHING at some point (e.g. both "Elementary"
  // and "Elementary Department" existing as separate records after a
  // rename) — every real section lives under only ONE of each pair, so
  // the other always shows a 0 count and is pure clutter in the tab row.
  // Hide a department tab once it has no sections for the selected
  // school year, UNLESS it's the one currently active — switching
  // School Year should never make the tab you're standing on vanish out
  // from under you. Purely a display filter: doesn't touch the
  // department records themselves.
  // Departments have no ordering field of their own, so "school
  // progression" order (Pre-School → Elementary → Junior High → Senior
  // High) is derived from the grade levels underneath each one — same
  // `ordering` field the Grade Level groupby mode already sorts by.
  // Using the LOWEST ordering among a department's own grade levels
  // means e.g. "Pre-School" sorts first because it contains Nursery
  // (ordering 0), not because of anything about the department record
  // itself. A department with no grade levels yet sorts last rather
  // than crashing the comparison.
  const minGradeOrderingByDept = useMemo(() => {
    const map = new Map();
    for (const gl of gradeLevels) {
      if (gl.departmentId == null) continue;
      const cur = map.get(gl.departmentId);
      const ord = gl.ordering ?? 0;
      if (cur == null || ord < cur) map.set(gl.departmentId, ord);
    }
    return map;
  }, [gradeLevels]);

  const visibleLevelTabs = useMemo(() => {
    return departments
      .filter(d => (sectionCountByLevel.get(d.id) || 0) > 0 || String(d.id) === String(levelId))
      .slice()
      .sort((a, b) => {
        const oa = minGradeOrderingByDept.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const ob = minGradeOrderingByDept.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return oa !== ob ? oa - ob : (a.name || "").localeCompare(b.name || "");
      });
  }, [departments, sectionCountByLevel, levelId, minGradeOrderingByDept]);

  const activeSem = semId ? semesterById[semId] : null;
  const activeSy  = syId  ? syById[syId]        : null;

  /** Offerings in the currently-selected semester only. That's the scope the
   *  whole panel operates on — teacher cards, detail modal, everything. */
  const offeringsInSem = useMemo(
    () => offerings.filter(o => !semId || String(o.semesterId) === String(semId)),
    [offerings, semId]
  );

  /** `offeringsInSem` narrowed further to the selected Level (or
   *  identical to it when no Level is picked). This is the base every
   *  group-by mode and the Grid view build from, so picking "Elementary"
   *  hides offerings for every other level everywhere at once instead
   *  of needing each mode to re-implement the same check. */
  const offeringsInScope = useMemo(
    () => offeringsInSem.filter(o => sectionInLevel(sectionById[o.sectionId])),
    // sectionInLevel/gradeInLevel are plain functions redefined every
    // render that close over levelId/gradeLevelById — depending on
    // those underlying values directly (already listed) is equivalent
    // to depending on the functions themselves, without needing to
    // useCallback-wrap two trivial one-line lookups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offeringsInSem, sectionById, levelId, gradeId, gradeLevelById]
  );

  /** Map<sectionId, count> of ENROLLED students for the active SY +
   *  Semester. Drives the "{N} students" pill on section-mode cards.
   *  Deduped by studentId so a student with multiple enrollment rows
   *  for the same section/term only counts once. */
  const studentsBySectionInTerm = useMemo(() => {
    const seen = new Map(); // sectionId -> Set<studentId>
    for (const e of (enrollments || [])) {
      if (String(e.status || "").toUpperCase() !== "ENROLLED") continue;
      if (syId  && String(e.schoolYearId) !== String(syId))  continue;
      if (semId && String(e.semesterId)   !== String(semId)) continue;
      if (e.sectionId == null || e.studentId == null) continue;
      let set = seen.get(e.sectionId);
      if (!set) { set = new Set(); seen.set(e.sectionId, set); }
      set.add(e.studentId);
    }
    const out = new Map();
    for (const [k, v] of seen) out.set(k, v.size);
    return out;
  }, [enrollments, syId, semId]);

  /** Offerings grouped by teacher (or an "Unassigned" bucket for teacherless
   *  offerings). Also used by the TeacherDetailModal drill-down. */
  const groupedByTeacher = useMemo(() => {
    const map = new Map();
    for (const o of offeringsInScope) {
      const key = o.teacherStaffId ?? "unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return map;
  }, [offeringsInScope]);

  /** Rows for the current group-by mode. Each row: {key, mode, entity, name,
   *  meta, offs, isEmpty, isOrphan}. `isEmpty` = no classes at all (gap);
   *  `isOrphan` = the special "offerings with no teacher" bucket (teacher mode). */
  const rows = useMemo(() => {
    const out = [];
    if (groupBy === "teacher") {
      // Existing: bucket by teacher + orphan bucket for teacherless offerings.
      for (const [key, offs] of groupedByTeacher.entries()) {
        const teacher = key === "unassigned" ? null : employeeById[key];
        const name    = teacher?.name || "— Unassigned —";
        out.push({
          key: `t-${key}`,
          mode: "teacher",
          entity: teacher,
          name,
          meta: teacher?.role || (key === "unassigned" ? "Missing teacher" : "Teacher"),
          offs,
          isEmpty: false,
          isOrphan: key === "unassigned",
        });
      }
      if (showEmpty) {
        // Also surface teachers with zero classes this semester.
        const teachers = employees.filter(e => ["TEACHING","TEACHER"].includes(e.staffType));
        for (const t of teachers) {
          if (!groupedByTeacher.has(t.id)) {
            out.push({
              key: `t-${t.id}`,
              mode: "teacher",
              entity: t,
              name: t.name,
              meta: t.role || "Teacher",
              offs: [],
              isEmpty: true,
              isOrphan: false,
            });
          }
        }
      }
    } else if (groupBy === "subject") {
      for (const subj of subjects) {
        if (!gradeInLevel(gradeLevelById[subj.gradeLevelId])) continue;
        const offs = offeringsInScope.filter(o => o.subjectId === subj.id);
        const isEmpty = offs.length === 0;
        if (isEmpty && !showEmpty) continue;
        out.push({
          key: `s-${subj.id}`,
          mode: "subject",
          entity: subj,
          name: `${subj.code} · ${subj.title}`,
          meta: "Subject",
          offs,
          isEmpty,
          isOrphan: false,
        });
      }
    } else if (groupBy === "section") {
      for (const sec of sections) {
        if (!sectionInLevel(sec)) continue;
        const offs = offeringsInScope.filter(o => o.sectionId === sec.id);
        const isEmpty = offs.length === 0;
        if (isEmpty && !showEmpty) continue;
        out.push({
          key: `sec-${sec.id}`,
          mode: "section",
          entity: sec,
          name: sec.name,
          meta: "Section",
          offs,
          isEmpty,
          isOrphan: false,
        });
      }
    } else if (groupBy === "grade") {
      // Walk grade levels in their configured order. An offering belongs to a
      // grade via its section's gradeLevelId, so we build a section→grade
      // lookup once and bucket the semester's offerings.
      const sectionGrade = new Map(sections.map(sec => [sec.id, sec.gradeLevelId]));
      const sorted = gradeLevels.slice().sort(
        (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0) || (a.name || "").localeCompare(b.name || "")
      );
      for (const gl of sorted) {
        if (!gradeInLevel(gl)) continue;
        const offs = offeringsInScope.filter(o => sectionGrade.get(o.sectionId) === gl.id);
        const isEmpty = offs.length === 0;
        if (isEmpty && !showEmpty) continue;
        out.push({
          key: `gl-${gl.id}`,
          mode: "grade",
          entity: gl,
          name: gl.name,
          meta: "Grade Level",
          offs,
          isEmpty,
          isOrphan: false,
        });
      }
    }
    return out;
    // gradeInLevel/sectionInLevel close over levelId/gradeLevelById,
    // both already listed below — see the matching note on
    // offeringsInScope above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, showEmpty, groupedByTeacher, offeringsInScope, subjects, sections, gradeLevels,
      employees, employeeById, levelId, gradeId, gradeLevelById]);

  /** Apply search across every mode. Matches the row's display name AND
   *  any field of its child offerings (subject, section, teacher, schedule,
   *  room) so the admin can type anything. */
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter(row => {
        if (row.name.toLowerCase().includes(q)) return true;
        return row.offs.some(o => {
          const subj = subjectById[o.subjectId];
          const sec  = sectionById[o.sectionId];
          const tea  = o.teacherStaffId ? employeeById[o.teacherStaffId] : null;
          return (subj?.code || "").toLowerCase().includes(q)
              || (subj?.title || "").toLowerCase().includes(q)
              || (sec?.name || "").toLowerCase().includes(q)
              || (tea?.name || "").toLowerCase().includes(q)
              || (o.schedule || "").toLowerCase().includes(q)
              || roomName(o, roomById).toLowerCase().includes(q);
        });
      });
    }
    return out.slice().sort((a, b) => {
      // Orphan bucket (offerings without a teacher) always first — urgent gap.
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? -1 : 1;
      // Then empty items (gaps) — draws attention to things needing a teacher.
      if (a.isEmpty !== b.isEmpty) return a.isEmpty ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [rows, search, subjectById, sectionById, employeeById, roomById]);

  const detailTeacher = detailTeacherId === null
    ? null
    : detailTeacherId === "unassigned"
      ? { id: "unassigned", name: "— Unassigned —" }
      : employeeById[detailTeacherId];
  const detailOffs = detailTeacherId === null
    ? []
    : (groupedByTeacher.get(detailTeacherId) || []);

  return (
    <div className={s.wrap}>
      {/* ── Filter bar ────────────────────────────────────────────────── */}
      <div className={s.filterRow}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)}>
            {semesters
              .filter(x => !syId || String(x.schoolYearId) === String(syId))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Grade Level</span>
          <select value={gradeId} onChange={e => setGradeId(e.target.value)}>
            <option value="">All grades</option>
            {gradeOptions.map(gl => (
              <option key={gl.id} value={gl.id}>{gl.name}</option>
            ))}
          </select>
        </label>
        <label className={`${s.filterField} ${s.searchField}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                 className={s.searchIcon} aria-hidden>
              <circle cx="9" cy="9" r="6" />
              <path d="M15 15l-3.5-3.5" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Teacher, subject, section, schedule…"
            />
            {search && (
              <button type="button" className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
            )}
          </div>
        </label>
        <button className={s.newBtn} onClick={() => { setNewTeacherId(""); setShowNew(true); }} disabled={!semId}>
          + New
        </button>
      </div>

      {/* ── Level tabs — narrows the whole panel (List and Grid both) to one
            school level at a time. Without this, Section/Grade Level
            grouping just lists all sections alphabetically with no way to
            tell a Nursery row from a Grade Ten row at a glance. */}
      <div className={s.groupByWrap}>
        <span className={s.segLabel}>Level</span>
        <div className={s.segmented} role="tablist" aria-label="School level">
          <button
            type="button"
            role="tab"
            aria-selected={!levelId}
            className={`${s.segBtn} ${!levelId ? s.segBtnActive : ""}`}
            onClick={() => setLevelId("")}>
            All
            <span className={s.segBtnCount}>{sectionCountByLevel.get("__all__") || 0}</span>
          </button>
          {visibleLevelTabs.map(d => (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={String(levelId) === String(d.id)}
              className={`${s.segBtn} ${String(levelId) === String(d.id) ? s.segBtnActive : ""}`}
              onClick={() => setLevelId(String(d.id))}>
              {/* "Pre-School Department" → "Pre-School": the row is already labelled Level. */}
              {(d.name || "").replace(/\s*department\s*$/i, "")}
              <span className={s.segBtnCount}>{sectionCountByLevel.get(d.id) || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── View controls: group-by + show-empty toggle ─────────────────── */}
      <div className={s.controlRow}>
        {/* View toggle — List (current cards) ↔ Grid (Mon–Fri planner).
            Lives at the top of the control row so it's the first decision
            the user makes. The group-by sub-controls only apply to List
            view; they're hidden in Grid mode to reduce noise. */}
        <div className={s.groupByWrap}>
          <span className={s.segLabel}>View</span>
          <div className={s.segmented} role="tablist" aria-label="View mode">
            {[
              ["list", "List"],
              ["grid", "Grid"],
            ].map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                role="tab"
                aria-selected={viewMode === val}
                className={`${s.segBtn} ${viewMode === val ? s.segBtnActive : ""}`}
                onClick={() => setViewMode(val)}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {viewMode === "list" && (
          <>
            <div className={s.groupByWrap}>
              <span className={s.segLabel}>Group by</span>
              <div className={s.segmented} role="tablist" aria-label="Group by">
                {[
                  ["teacher", "Teacher"],
                  ["subject", "Subject"],
                  ["section", "Section"],
                  ["grade",   "Grade Level"],
                ].map(([val, lbl]) => (
                  <button
                    key={val}
                    type="button"
                    role="tab"
                    aria-selected={groupBy === val}
                    className={`${s.segBtn} ${groupBy === val ? s.segBtnActive : ""}`}
                    onClick={() => setGroupBy(val)}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <label className={s.toggle}>
              <input
                type="checkbox"
                checked={showEmpty}
                onChange={e => setShowEmpty(e.target.checked)}
              />
              <span>Show unassigned (no classes yet)</span>
            </label>
          </>
        )}
      </div>

      {err && <div className={s.error}>{err}</div>}

      {/* ── Grid view — premium Mon–Fri planner. Reads the same
            offeringsInScope (semester + Level filter) so the two views
            stay in lockstep, and calls loadAll on save so the list view
            sees the change too if the user toggles back. ── */}
      {viewMode === "grid" && !loading && semId && (
        <TeachingGridView
          semesterId={Number(semId)}
          semesterLabel={activeSem ? activeSem.label : ""}
          employees={employees}
          subjects={subjects}
          sections={sections}
          offerings={offeringsInScope}
          onOfferingsChanged={loadAll}
        />
      )}
      {viewMode === "grid" && (loading || !semId) && (
        <div className={loading ? s.loading : s.empty}>
          {loading ? "Loading…" : "Pick a school year and semester to plan the schedule."}
        </div>
      )}

      {/* ── List view: original card grid ─────────────────────────────── */}
      {viewMode === "list" && (loading ? (
        <div className={s.loading}>Loading…</div>
      ) : !semId ? (
        <div className={s.empty}>
          Pick a school year and semester to see class offerings.
        </div>
      ) : filteredRows.length === 0 ? (
        <div className={s.empty}>
          {search
            ? "No matches. Try clearing the search."
            : "No class offerings yet for this semester. Click + New to add some."}
        </div>
      ) : (
        <div className={s.cardGrid}>
          {filteredRows.map(row => {
            const subjectCount = new Set(row.offs.map(o => o.subjectId)).size;
            const sectionCount = new Set(row.offs.map(o => o.sectionId)).size;
            const teacherCount = new Set(
              row.offs.map(o => o.teacherStaffId).filter(x => x != null)
            ).size;

            // Pills vary by mode — each group answers a different question.
            let pills;
            if (row.mode === "teacher") {
              pills = row.isEmpty ? (
                <span className={s.pillWarn}>No classes yet</span>
              ) : (
                <>
                  <span className={s.pill}>{row.offs.length} class{row.offs.length === 1 ? "" : "es"}</span>
                  <span className={s.pillMuted}>{subjectCount} subject{subjectCount === 1 ? "" : "s"}</span>
                  <span className={s.pillMuted}>{sectionCount} section{sectionCount === 1 ? "" : "s"}</span>
                </>
              );
            } else if (row.mode === "subject") {
              pills = row.isEmpty ? (
                <span className={s.pillWarn}>No teacher assigned</span>
              ) : (
                <>
                  <span className={s.pill}>{row.offs.length} class{row.offs.length === 1 ? "" : "es"}</span>
                  <span className={s.pillMuted}>{teacherCount} teacher{teacherCount === 1 ? "" : "s"}</span>
                  <span className={s.pillMuted}>{sectionCount} section{sectionCount === 1 ? "" : "s"}</span>
                </>
              );
            } else if (row.mode === "grade") {
              pills = row.isEmpty ? (
                <span className={s.pillWarn}>No classes scheduled</span>
              ) : (
                <>
                  <span className={s.pill}>{row.offs.length} class{row.offs.length === 1 ? "" : "es"}</span>
                  <span className={s.pillMuted}>{sectionCount} section{sectionCount === 1 ? "" : "s"}</span>
                  <span className={s.pillMuted}>{subjectCount} subject{subjectCount === 1 ? "" : "s"}</span>
                  <span className={s.pillMuted}>{teacherCount} teacher{teacherCount === 1 ? "" : "s"}</span>
                </>
              );
            } else {
              // Section mode — surface the roster size right on the card so
              // admins don't need to drill in just to see counts. Zero
              // students gets the warn tone so unfilled sections stand out.
              const studentCount = row.entity?.id != null
                ? (studentsBySectionInTerm.get(row.entity.id) || 0)
                : 0;
              pills = row.isEmpty ? (
                <>
                  <span className={s.pillWarn}>No classes scheduled</span>
                  <span className={studentCount === 0 ? s.pillStudentsWarn : s.pillStudents}>
                    {studentCount} student{studentCount === 1 ? "" : "s"}
                  </span>
                </>
              ) : (
                <>
                  <span className={studentCount === 0 ? s.pillStudentsWarn : s.pillStudents}>
                    {studentCount} student{studentCount === 1 ? "" : "s"}
                  </span>
                  <span className={s.pill}>{row.offs.length} class{row.offs.length === 1 ? "" : "es"}</span>
                  <span className={s.pillMuted}>{subjectCount} subject{subjectCount === 1 ? "" : "s"}</span>
                  <span className={s.pillMuted}>{teacherCount} teacher{teacherCount === 1 ? "" : "s"}</span>
                </>
              );
            }

            const teacherPhoto = row.mode === "teacher" ? row.entity?.photoUrl : null;
            const avatarLabel = row.mode === "subject"
              ? (row.entity?.code || "?")
              : initials(row.name);
            const warn = row.isOrphan || row.isEmpty;

            function handleClick() {
              if (row.mode === "teacher") {
                // The "— Unassigned —" bucket has no teacher to assign to,
                // so it keeps the read/edit list of teacherless classes.
                if (row.isOrphan) { setDetailTeacherId("unassigned"); return; }
                // Every real teacher opens one dialog: their existing
                // classes on top (view / edit / delete), new rows below.
                setNewTeacherId(row.entity?.id ?? "");
                setShowNew(true);
              } else {
                // Subject / Section: read-only drill-down, or batch modal if empty.
                if (row.isEmpty) {
                  setNewTeacherId("");
                  setShowNew(true);
                  return;
                }
                setDetailGroup({ mode: row.mode, entity: row.entity, name: row.name, offs: row.offs });
              }
            }

            return (
              <button
                key={row.key}
                type="button"
                className={`${s.teacherCard} ${warn ? s.teacherCardWarn : ""}`}
                onClick={handleClick}>
                {teacherPhoto
                  ? <img src={teacherPhoto} alt="" className={s.avatarImg} />
                  : <div className={s.avatar} style={{ background: avatarColor(row.name) }}>
                      {avatarLabel}
                    </div>
                }
                <div className={s.cardBody}>
                  <div className={s.cardName}>{row.name}</div>
                  <div className={s.cardMeta}>{row.meta}</div>
                  <div className={s.cardPills}>{pills}</div>
                </div>
                <span className={s.cardChevron} aria-hidden>›</span>
              </button>
            );
          })}
        </div>
      ))}

      {/* ── Modals ────────────────────────────────────────────────────── */}
      {showNew && (
        <NewBatchModal
          schoolYears={schoolYears}
          semesters={semesters}
          subjects={subjects}
          sections={sections}
          employees={employees}
          offerings={offerings}
          gradeLevels={gradeLevels}
          roomById={roomById}
          initialSyId={syId}
          initialSemId={semId}
          initialTeacherId={newTeacherId}
          students={students}
          enrollments={enrollments}
          onClose={() => setShowNew(false)}
          // Stays open after saving — the new classes move up into
          // "Already assigned" once the reload lands.
          onSaved={loadAll}
        />
      )}

      {detailTeacher && (
        <TeacherDetailModal
          teacher={detailTeacher}
          offerings={detailOffs}
          roomById={roomById}
          subjects={subjects}
          sections={sections}
          activeSy={activeSy}
          activeSem={activeSem}
          // Forwarded for the inline "View students" roster modal —
          // these are already loaded at the panel level, so we don't
          // refetch and the roster opens instantly with full context.
          students={students}
          enrollments={enrollments}
          gradeLevels={gradeLevels}
          schoolYears={schoolYears}
          semesters={semesters}
          onClose={() => setDetailTeacherId(null)}
          onChanged={async () => { await loadAll(); }}
          onAddMore={() => {
            setNewTeacherId(detailTeacherId === "unassigned" ? "" : detailTeacherId);
            setDetailTeacherId(null);
            setShowNew(true);
          }}
        />
      )}

      {detailGroup && (
        <GroupDetailModal
          mode={detailGroup.mode}
          entity={detailGroup.entity}
          title={detailGroup.name}
          offerings={detailGroup.offs}
          subjectById={subjectById}
          sectionById={sectionById}
          employeeById={employeeById}
          roomById={roomById}
          activeSy={activeSy}
          activeSem={activeSem}
          students={students}
          enrollments={enrollments}
          sections={sections}
          gradeLevels={gradeLevels}
          schoolYears={schoolYears}
          semesters={semesters}
          onManageRoster={(sectionId) => {
            // One-shot deep-link handoff to Directory > Enrollments tab,
            // pre-filtered to the section + current term. The Directory
            // page reads + applies + clears this on mount.
            try {
              sessionStorage.setItem("directory.deeplink", JSON.stringify({
                tab: "enrollments",
                sectionId,
                schoolYearId: activeSy?.id ?? null,
                semesterId:   activeSem?.id ?? null,
              }));
            } catch { /* sessionStorage unavailable — proceed without filter */ }
            navigate("/directory");
          }}
          onClose={() => setDetailGroup(null)}
          onAddMore={() => { setDetailGroup(null); setNewTeacherId(""); setShowNew(true); }}
        />
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
   Teacher detail modal — lists a teacher's classes for the active
   semester, with inline edit / delete. Keep add-flow delegated to the
   batch modal to avoid duplicating the form.
   ═══════════════════════════════════════════════════════════════════ */
function TeacherDetailModal({
  teacher, offerings, subjects, sections, activeSy, activeSem,
  roomById = {},
  // Optional context arrays — forwarded to the inline SectionRosterModal
  // so a click on "View students" can render the full premium roster
  // (and its embedded student-detail modal) without any extra fetch.
  // Each defaults to [] so the modal still renders cleanly if a caller
  // forgets to pass them — degraded but never broken.
  students    = [],
  enrollments = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  onClose, onChanged, onAddMore,
}) {
  const subjectById = Object.fromEntries(subjects.map(x => [x.id, x]));
  const sectionById = Object.fromEntries(sections.map(x => [x.id, x]));
  const gradeLevelById = Object.fromEntries(gradeLevels.map(x => [x.id, x]));
  const [editingId, setEditingId] = useState(null);
  const [editForm,  setEditForm]  = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState("");
  // Roster pop-up — opened from each class row's "View students" button.
  // Stored as a {section, grade, schoolYear, semester} bundle so the
  // SectionRosterModal renders with the right header context.
  const [rosterFor, setRosterFor] = useState(null);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function openRosterFor(o) {
    const sec   = sectionById[o.sectionId];
    if (!sec) return;
    const grade = sec.gradeLevelId ? gradeLevelById[sec.gradeLevelId] : null;
    setRosterFor({
      section:        { id: sec.id, name: sec.name },
      gradeLabel:     grade?.name || null,
      schoolYearId:   activeSy?.id    || null,
      semesterId:     activeSem?.id   || null,
      schoolYearLabel: activeSy?.label || null,
      semesterLabel:   activeSem?.label || null,
    });
  }

  function startEdit(o) {
    setEditingId(o.id);
    setEditForm({
      subjectId: String(o.subjectId ?? ""),
      sectionId: String(o.sectionId ?? ""),
      schedule:  o.schedule || "",
      // Prefer the new roomId; the legacy text `room` is preserved
      // server-side and surfaces in renderCell as a fallback for rows
      // not yet migrated.
      roomId:    o.roomId ?? null,
      room:      o.room   || "",
    });
    setErr("");
  }

  async function saveEdit(o) {
    if (!editForm.subjectId || !editForm.sectionId) {
      setErr("Subject and Section are required."); return;
    }
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${o.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          semesterId:     o.semesterId,
          teacherStaffId: o.teacherStaffId,
          subjectId:      Number(editForm.subjectId),
          sectionId:      Number(editForm.sectionId),
          schedule:       editForm.schedule.trim() || null,
          // Send both: roomId is the new authoritative ref; text room is
          // kept null (the legacy field is preserved on the server's
          // existing row only when the admin doesn't clear it explicitly,
          // but going forward roomId is canonical).
          roomId:         editForm.roomId ?? null,
          room:           editForm.room?.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      await onChanged();
      setEditingId(null); setEditForm(null);
    } catch (e) { setErr(e.message); }
    finally    { setBusy(false); }
  }

  async function removeOne(o) {
    if (!window.confirm("Remove this class?")) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${o.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Delete failed (${res.status})`));
      await onChanged();
    } catch (e) { setErr(e.message); }
    finally    { setBusy(false); }
  }

  // Portaled to <body> so position:fixed centers on the viewport rather
  // than on .shell (whose transform creates a fixed-positioning containing
  // block that would otherwise push the modal off-screen).
  return createPortal(
    <div className={s.overlay}>
      <div className={`${s.modal} ${s.detailModal}`}>
        <div className={s.modalHead}>
          <div className={s.modalHeadMain}>
            {teacher?.photoUrl
              ? <img src={teacher.photoUrl} alt="" className={s.modalAvatarImg} />
              : <div className={s.modalAvatar} style={{ background: avatarColor(teacher?.name) }}>
                  {initials(teacher?.name)}
                </div>
            }
            <div>
              <div className={s.modalTitle}>{teacher?.name}</div>
              <div className={s.modalSub}>
                {activeSy?.label ? syLabel(activeSy.label) : "—"}
                {activeSem?.label ? ` · ${activeSem.label}` : ""}
                {" · "}
                {offerings.length} class{offerings.length === 1 ? "" : "es"}
              </div>
            </div>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {err && <div className={s.error}>{err}</div>}

        {/* Clarifying note — answers the most common admin question
            ("how do I add students to this class?"). Students belong
            to the SECTION, not the class offering, so the right path
            is via Directory → enrolment. The "View students" link on
            each row below opens the section roster inline for quick
            context. */}
        {offerings.length > 0 && (
          <div className={s.detailHint}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8"  x2="12" y2="8.01"/>
            </svg>
            <span>
              <strong>Students belong to the section, not the class.</strong>{" "}
              To enroll a student in any of these classes, add them to the
              relevant section in <em>Directory → Enrollments</em>. Use{" "}
              <em>View students</em> on a row to peek at who's currently in
              that section.
            </span>
          </div>
        )}

        {offerings.length === 0 ? (
          <div className={s.detailEmpty}>No classes assigned for this semester.</div>
        ) : (
          <ul className={s.detailList}>
            {offerings.map(o => {
              const subj = subjectById[o.subjectId];
              const sec  = sectionById[o.sectionId];
              const isEditing = editingId === o.id;
              if (isEditing) {
                return (
                  <li key={o.id} className={`${s.detailRow} ${s.detailRowEditing}`}>
                    <select
                      className={s.editInput}
                      value={editForm.subjectId}
                      onChange={e => setEditForm(f => ({ ...f, subjectId: e.target.value }))}>
                      <option value="">— Subject —</option>
                      {subjects.map(x => (
                        <option key={x.id} value={x.id}>{x.code} · {x.title}</option>
                      ))}
                    </select>
                    <select
                      className={s.editInput}
                      value={editForm.sectionId}
                      onChange={e => setEditForm(f => ({ ...f, sectionId: e.target.value }))}>
                      <option value="">— Section —</option>
                      <SectionOptions sections={sections} gradeLevels={gradeLevels} />
                    </select>
                    <SchedulePicker
                      value={editForm.schedule}
                      onChange={v => setEditForm(f => ({ ...f, schedule: v }))}
                    />
                    <RoomPicker
                      value={editForm.roomId}
                      onChange={(roomId) => setEditForm(f => ({ ...f, roomId }))}
                      placeholder={editForm.room ? `Legacy: ${editForm.room} (pick a room)` : "Pick a room…"}
                    />
                    <div className={s.detailActions}>
                      <button className={s.btnGhost} disabled={busy}
                              onClick={() => { setEditingId(null); setEditForm(null); }}>
                        Cancel
                      </button>
                      <button className={s.btnPrimary} disabled={busy}
                              onClick={() => saveEdit(o)}>
                        {busy ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </li>
                );
              }
              return (
                <li key={o.id} className={s.detailRow}>
                  <div className={s.detailCode}>{subj?.code || "?"}</div>
                  <div className={s.detailTitle}>
                    <div>{subj?.title || "—"}</div>
                    <div className={s.detailMeta}>
                      {sec?.name || "—"}
                      {sec?.gradeLevelId && gradeLevelById[sec.gradeLevelId] ? ` · ${gradeLevelById[sec.gradeLevelId].name}` : ""}
                    </div>
                  </div>
                  {(() => {
                    // Break the schedule string ("Mon/Wed 08:00-09:00") into
                    // its day and time parts so they can live in separate
                    // grid columns — keeps times vertically aligned even
                    // when day counts differ across rows.
                    const raw = o.schedule || "";
                    const idx = raw.lastIndexOf(" ");
                    const days = idx === -1 ? (raw || "") : raw.slice(0, idx);
                    const time = idx === -1 ? ""          : raw.slice(idx + 1);
                    return (
                      <>
                        <div className={s.detailDays}>{days || "—"}</div>
                        <div className={s.detailTime}>{time || "—"}</div>
                      </>
                    );
                  })()}
                  <div className={s.detailRoom}>{roomName(o, roomById) || "—"}</div>
                  <div className={s.detailActions}>
                    {/* "View students" — opens the section's roster
                        modal inline so the admin can see who's enrolled
                        without bouncing through Academic Directory.
                        Always shown (even for unassigned-teacher rows)
                        because it's a read-only deep-link, no mutations. */}
                    <button
                      type="button"
                      className={s.viewRosterBtn}
                      onClick={() => openRosterFor(o)}
                      disabled={busy || !sectionById[o.sectionId]}
                      title={sectionById[o.sectionId]
                        ? `View students enrolled in ${sec?.name || "this section"}`
                        : "Section unresolved"}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      View students
                    </button>
                    {teacher?.id !== "unassigned" && (
                      <>
                        <button className={s.btnGhost} onClick={() => startEdit(o)} disabled={busy}>Edit</button>
                        <button className={s.btnDanger} onClick={() => removeOne(o)} disabled={busy}>Delete</button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className={s.modalFoot}>
          {teacher?.id !== "unassigned" && (
            <button className={s.btnPrimary} onClick={onAddMore}>+ Add more classes</button>
          )}
        </div>

        {/* Inline section-roster modal. Renders only when a row's
            "View students" was clicked; closing returns the admin to
            the teacher detail without losing scroll or other state.
            Reuses the same SectionRosterModal the dashboard + Academic
            Directory use, so the look-and-feel is identical app-wide. */}
        <SectionRosterModal
          open={!!rosterFor}
          section={rosterFor?.section}
          gradeLabel={rosterFor?.gradeLabel}
          schoolYearId={rosterFor?.schoolYearId}
          semesterId={rosterFor?.semesterId}
          schoolYearLabel={rosterFor?.schoolYearLabel}
          semesterLabel={rosterFor?.semesterLabel}
          students={students}
          enrollments={enrollments}
          sections={sections}
          gradeLevels={gradeLevels}
          schoolYears={schoolYears}
          semesters={semesters}
          onClose={() => setRosterFor(null)}
        />
      </div>
    </div>,
    document.body
  );
}

/* ═════════════════════════════════════════════════════════════════════
   Batch-add modal — the main usability win. Pick SY + Semester once,
   pick a teacher once, then rattle off as many (Subject, Section,
   Schedule, Room) rows as you want. Everything POSTs in parallel.
   ═══════════════════════════════════════════════════════════════════ */
function NewBatchModal({
  schoolYears, semesters, subjects, sections, employees,
  offerings = [], gradeLevels = [], roomById = {},
  students = [], enrollments = [],
  initialSyId, initialSemId, initialTeacherId, onClose, onSaved,
}) {
  const [notice, setNotice] = useState("");
  const [syId,   setSyId]   = useState(initialSyId  || "");
  const [semId,  setSemId]  = useState(initialSemId || "");
  const [teacherId, setTeacherId] = useState(initialTeacherId ? String(initialTeacherId) : "");
  // Existing class clicked in the "Already assigned" list → details popup.
  const [viewing, setViewing] = useState(null);

  /** Classes this teacher already has in the picked semester — shown above
   *  the new rows so the admin doesn't double-book or re-add a class. */
  const assigned = useMemo(() => {
    if (!teacherId || !semId) return [];
    const subjectTitle = id => subjects.find(x => x.id === id)?.title || "";
    return offerings
      .filter(o => String(o.teacherStaffId) === String(teacherId)
                && String(o.semesterId)     === String(semId))
      .slice()
      .sort((a, b) => subjectTitle(a.subjectId).localeCompare(subjectTitle(b.subjectId)));
  }, [offerings, subjects, teacherId, semId]);

  /** `assigned` bucketed by the section's grade level, in school order —
   *  a teacher may cover several grades (even several levels). */
  const assignedByGrade = useMemo(() => {
    const groups = new Map(); // gradeLevelId | "none" -> offerings
    for (const o of assigned) {
      const sec = sections.find(x => x.id === o.sectionId);
      const key = sec?.gradeLevelId ?? "none";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(o);
    }
    const out = gradeLevels.slice().sort(byGradeOrder)
      .filter(gl => groups.has(gl.id))
      .map(gl => ({ key: gl.id, name: gl.name, offs: groups.get(gl.id) }));
    const known = new Set(out.map(g => g.key));
    const rest = [...groups.entries()].filter(([k]) => !known.has(k)).flatMap(([, v]) => v);
    if (rest.length) out.push({ key: "none", name: "No grade level", offs: rest });
    return out;
  }, [assigned, sections, gradeLevels]);

  /** "subjectId|sectionId" → {teacherName} for every class already offered
   *  in the picked semester, whoever teaches it. */
  const takenBy = useMemo(() => {
    const map = new Map();
    if (!semId) return map;
    for (const o of offerings) {
      if (String(o.semesterId) !== String(semId)) continue;
      const t = employees.find(e => String(e.id) === String(o.teacherStaffId));
      map.set(`${o.subjectId}|${o.sectionId}`, { teacherName: t?.name || "(no teacher yet)" });
    }
    return map;
  }, [offerings, employees, semId]);

  /** Sections for the picked school year (sections are per-SY). */
  const sectionsInSy = useMemo(
    () => sections.filter(x => !syId || x.schoolYearId == null || String(x.schoolYearId) === String(syId)),
    [sections, syId]
  );
  const [rows,   setRows]   = useState([blankRow()]);
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState("");

  const teachers = useMemo(
    () => employees.filter(e => e.staffType === "TEACHING"),
    [employees]
  );

  const semestersInSy = useMemo(
    () => semesters.filter(x => !syId || String(x.schoolYearId) === String(syId)),
    [semesters, syId]
  );

  // If the SY changes, pick a valid semester.
  useEffect(() => {
    if (!semestersInSy.some(x => String(x.id) === semId)) {
      const fresh = semestersInSy.find(x => x.active) || semestersInSy[0];
      setSemId(fresh ? String(fresh.id) : "");
    }
  }, [semestersInSy, semId]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== "Escape") return;
      // While the class popup is open it handles Escape itself.
      if (!viewing) onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, viewing]);

  function blankRow() {
    return { subjectId: "", sectionId: "", schedule: "", roomId: null };
  }

  function updateRow(i, patch) {
    setRows(rs => rs.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      // Subjects belong to a grade level, not a section directly — if the
      // section just changed and the row's current subject no longer
      // belongs to that section's grade, drop it rather than silently
      // leaving a Kindergarten subject paired with a Grade 7 section.
      if ("sectionId" in patch && next.subjectId) {
        const sec  = sections.find(x => String(x.id) === String(next.sectionId));
        const subj = subjects.find(x => String(x.id) === String(next.subjectId));
        if (sec && subj && String(subj.gradeLevelId) !== String(sec.gradeLevelId)) {
          next.subjectId = "";
        }
      }
      return next;
    }));
  }
  function addRow()      { setRows(rs => [...rs, blankRow()]); }
  function removeRow(i)  { setRows(rs => rs.length === 1 ? [blankRow()] : rs.filter((_, idx) => idx !== i)); }

  async function save() {
    if (!syId || !semId) { setErr("School Year and Semester are required."); return; }
    if (!teacherId)      { setErr("Pick a teacher first.");                  return; }
    const valid = rows.filter(r => r.subjectId && r.sectionId);
    if (valid.length === 0) {
      setErr("Add at least one class (Subject + Section are required).");
      return;
    }
    // Catch the same Subject + Section listed twice in this one batch —
    // the backend also rejects it per-row, but failing fast here means
    // one clear message instead of N-1 successes and a confusing
    // "X of Y saves failed" after the fact.
    const seen = new Set();
    for (const r of valid) {
      const key = `${r.subjectId}|${r.sectionId}`;
      if (seen.has(key)) {
        setErr("The same Subject + Section is listed more than once below — remove the duplicate row.");
        return;
      }
      seen.add(key);
      // Same rule the backend enforces: one class per Subject + Section
      // per semester. Name who already has it so the admin knows where to
      // go (reassign from that teacher's class list via Edit).
      const taken = takenBy.get(key);
      if (taken) {
        const subj = subjects.find(x => String(x.id) === String(r.subjectId));
        const sec  = sections.find(x => String(x.id) === String(r.sectionId));
        setErr(`${subj?.title || "This subject"} in ${sec?.name || "this section"} is already taught by `
             + `${taken.teacherName} this semester. To move it to this teacher, open ${taken.teacherName}'s `
             + `classes, click the class and use Edit to change the teacher.`);
        return;
      }
    }
    setBusy(true); setErr(""); setNotice("");
    try {
      // Issue all the POSTs in parallel. If any fails, the user sees the
      // error and can retry — successes stay saved (the backend has no
      // transactional endpoint for this batch).
      const results = await Promise.allSettled(
        valid.map(r => fetch(`${API_BASE}/api/class-offerings`, {
          method:  "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            semesterId:     Number(semId),
            teacherStaffId: Number(teacherId),
            subjectId:      Number(r.subjectId),
            sectionId:      Number(r.sectionId),
            schedule:       r.schedule.trim() || null,
            roomId:         r.roomId ?? null,
          }),
        }).then(async res => {
          if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
        }))
      );
      const failedRows = valid.filter((_, i) => results[i].status === "rejected");
      const savedCount = valid.length - failedRows.length;
      // Saved rows leave the form (they now show under "Already
      // assigned"); failed ones stay so they can be fixed and retried
      // without re-posting the ones that already went through.
      setRows(failedRows.length ? failedRows : [blankRow()]);
      if (savedCount > 0) {
        await onSaved();
        setNotice(`Saved ${savedCount} class${savedCount === 1 ? "" : "es"}.`);
      }
      if (failedRows.length > 0) {
        const first = results.find(x => x.status === "rejected");
        throw new Error(`${failedRows.length} of ${valid.length} saves failed: ${first.reason?.message || ""}`);
      }
    } catch (e) { setErr(e.message); }
    finally    { setBusy(false); }
  }

  // Portaled to <body> for the same containing-block reason as the
  // detail modal above.
  return createPortal(
    <div className={s.overlay}>
      <div className={`${s.modal} ${s.batchModal}`}>
        <div className={s.modalHead}>
          <div className={s.modalHeadMain}>
            <div>
              <div className={s.modalTitle}>Assign classes to a teacher</div>
              <div className={s.modalSub}>Pick the term and teacher once, then add as many subjects as needed.</div>
            </div>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Step 1: term + teacher */}
        <div className={s.batchHead}>
          <label className={s.filterField}>
            <span>School Year</span>
            <select value={syId} onChange={e => setSyId(e.target.value)}>
              <option value="">— Select —</option>
              {schoolYears.map(y => (
                <option key={y.id} value={y.id}>{y.label}{y.active ? " · Active" : ""}</option>
              ))}
            </select>
          </label>
          <label className={s.filterField}>
            <span>Semester</span>
            <select value={semId} onChange={e => setSemId(e.target.value)} disabled={!syId}>
              <option value="">— Select —</option>
              {semestersInSy.map(x => (
                <option key={x.id} value={x.id}>{x.label}{x.active ? " · Active" : ""}</option>
              ))}
            </select>
          </label>
          <label className={s.filterField}>
            <span>Teacher</span>
            <select value={teacherId} onChange={e => setTeacherId(e.target.value)}>
              <option value="">— Select a teacher —</option>
              {teachers
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        </div>

        {/* Classes the picked teacher already has this semester. */}
        {teacherId && (
          <div className={s.assignedSection}>
            <div className={s.batchSectionTitle}>
              Already assigned · {assigned.length} class{assigned.length === 1 ? "" : "es"}
            </div>
            {assigned.length === 0 ? (
              <div className={s.assignedEmpty}>No classes yet for this semester.</div>
            ) : (
              <div className={s.assignedList}>
                {assignedByGrade.map(g => (
                  <div key={g.key} className={s.assignedGroup}>
                    <div className={s.assignedGroupHead}>
                      <span>{g.name}</span>
                      <span className={s.assignedGroupCount}>
                        {g.offs.length} class{g.offs.length === 1 ? "" : "es"}
                      </span>
                    </div>
                    <ul className={s.assignedRows}>
                      {g.offs.map(o => {
                        const subj = subjects.find(x => x.id === o.subjectId);
                        const sec  = sections.find(x => x.id === o.sectionId);
                        return (
                          <li key={o.id}>
                            <button type="button" className={s.assignedRow} onClick={() => setViewing(o)}>
                              <span className={s.assignedCode}>{subj?.code || "?"}</span>
                              <span className={s.assignedMain}>
                                <span className={s.assignedTitle}>{subj?.title || "—"}</span>
                                <span className={s.assignedMeta}>{sec?.name || "—"}</span>
                              </span>
                              <span className={s.assignedSched}>{o.schedule || "No schedule"}</span>
                              <span className={s.cardChevron} aria-hidden>›</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: class rows */}
        <div className={s.batchSection}>
          <div className={s.batchSectionTitle}>Classes to assign</div>
          <div className={s.batchRows}>
            {rows.map((r, i) => {
              // Subjects belong to a grade level, not a section — once a
              // section is picked, narrow the Subject list to that
              // section's own grade so a Kindergarten subject can't end
              // up paired with a Grade 7 section. Before a section is
              // picked there's nothing to narrow against yet, so show
              // everything.
              const sec = sections.find(x => String(x.id) === String(r.sectionId));
              const subjectOptions = sec
                ? subjects.filter(x => String(x.gradeLevelId) === String(sec.gradeLevelId))
                : subjects;
              return (
              <div key={i} className={s.batchRow}>
                <select
                  className={s.editInput}
                  value={r.sectionId}
                  onChange={e => updateRow(i, { sectionId: e.target.value })}>
                  <option value="">— Section —</option>
                  <SectionOptions sections={sectionsInSy} gradeLevels={gradeLevels} />
                </select>
                <select
                  className={s.editInput}
                  value={r.subjectId}
                  onChange={e => updateRow(i, { subjectId: e.target.value })}
                  disabled={sec && subjectOptions.length === 0}>
                  <option value="">
                    {sec && subjectOptions.length === 0 ? "No subjects for this grade yet" : "— Subject —"}
                  </option>
                  {subjectOptions.map(x => {
                    // A section gets one class per subject per semester —
                    // grey out subjects this section already has.
                    const taken = sec ? takenBy.get(`${x.id}|${sec.id}`) : null;
                    return (
                      <option key={x.id} value={x.id} disabled={!!taken}>
                        {x.code} · {x.title}{taken ? ` — taken by ${taken.teacherName}` : ""}
                      </option>
                    );
                  })}
                </select>
                <SchedulePicker
                  value={r.schedule}
                  onChange={v => updateRow(i, { schedule: v })}
                />
                <RoomPicker
                  value={r.roomId}
                  onChange={(roomId) => updateRow(i, { roomId })}
                  placeholder="Pick a room…"
                />
                <button
                  type="button"
                  className={s.removeBtn}
                  onClick={() => removeRow(i)}
                  aria-label="Remove row"
                  title="Remove row">✕</button>
              </div>
              );
            })}
          </div>
          <button type="button" className={s.btnGhost} onClick={addRow}>+ Add another class</button>
        </div>

        {err && <div className={s.error}>{err}</div>}
        {notice && !err && <div className={s.notice}>{notice}</div>}

        <div className={s.modalFoot}>
          <button className={s.btnGhost} onClick={onClose} disabled={busy}>Close</button>
          <button className={s.btnPrimary} onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save all classes"}
          </button>
        </div>
      </div>

      {viewing && (
        <AssignedClassModal
          offering={viewing}
          subjects={subjects}
          sections={sectionsInSy}
          allSections={sections}
          gradeLevels={gradeLevels}
          teachers={teachers}
          employees={employees}
          semesters={semesters}
          schoolYears={schoolYears}
          roomById={roomById}
          takenBy={takenBy}
          students={students}
          enrollments={enrollments}
          onClose={() => setViewing(null)}
          onChanged={async (msg) => { setViewing(null); await onSaved(); setErr(""); setNotice(msg); }}
        />
      )}
    </div>,
    document.body
  );
}

/* ═════════════════════════════════════════════════════════════════════
   One existing class, opened from "Already assigned" in the batch modal:
   details, View students, Edit (incl. moving it to another teacher) and
   Delete.
   ═══════════════════════════════════════════════════════════════════ */
function AssignedClassModal({
  offering: o, subjects, sections, allSections, gradeLevels, teachers, employees,
  semesters, schoolYears, roomById, takenBy, students, enrollments,
  onClose, onChanged,
}) {
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState("");
  const [roster,  setRoster]  = useState(false);

  const subj = subjects.find(x => x.id === o.subjectId);
  const sec  = allSections.find(x => x.id === o.sectionId);
  const gl   = sec ? gradeLevels.find(g => g.id === sec.gradeLevelId) : null;
  const teacher = employees.find(e => String(e.id) === String(o.teacherStaffId));
  const sem  = semesters.find(x => x.id === o.semesterId);
  const sy   = sem ? schoolYears.find(y => y.id === sem.schoolYearId) : null;

  useEffect(() => {
    function onKeyDown(e) {
      // The roster popup handles its own Escape.
      if (e.key === "Escape" && !roster) onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, roster]);

  function startEdit() {
    setForm({
      teacherStaffId: String(o.teacherStaffId ?? ""),
      sectionId:      String(o.sectionId ?? ""),
      subjectId:      String(o.subjectId ?? ""),
      schedule:       o.schedule || "",
      roomId:         o.roomId ?? null,
    });
    setErr("");
    setEditing(true);
  }

  // Same grade rule as the batch rows: a section change drops a subject
  // from another grade.
  function setSection(sectionId) {
    setForm(f => {
      const next = { ...f, sectionId };
      const s2 = allSections.find(x => String(x.id) === String(sectionId));
      const sb = subjects.find(x => String(x.id) === String(f.subjectId));
      if (s2 && sb && String(sb.gradeLevelId) !== String(s2.gradeLevelId)) next.subjectId = "";
      return next;
    });
  }

  async function saveEdit() {
    if (!form.teacherStaffId || !form.sectionId || !form.subjectId) {
      setErr("Teacher, Section and Subject are required."); return;
    }
    const key = `${form.subjectId}|${form.sectionId}`;
    if (key !== `${o.subjectId}|${o.sectionId}` && takenBy.get(key)) {
      setErr(`That subject is already offered in this section by ${takenBy.get(key).teacherName}.`); return;
    }
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${o.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          semesterId:     o.semesterId,
          teacherStaffId: Number(form.teacherStaffId),
          subjectId:      Number(form.subjectId),
          sectionId:      Number(form.sectionId),
          schedule:       form.schedule.trim() || null,
          roomId:         form.roomId ?? null,
          room:           o.room || null,
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      const moved = String(form.teacherStaffId) !== String(o.teacherStaffId);
      const to = employees.find(e => String(e.id) === String(form.teacherStaffId));
      await onChanged(moved ? `Class moved to ${to?.name || "the new teacher"}.` : "Class updated.");
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Remove this class?")) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${o.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Delete failed (${res.status})`));
      await onChanged("Class removed.");
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  const fields = [
    ["Subject",     subj ? `${subj.code} · ${subj.title}` : "—"],
    ["Section",     sec?.name || "—"],
    ["Grade Level", gl?.name  || "—"],
    ["Teacher",     teacher?.name || "—"],
    ["Term",        [sy?.label && syLabel(sy.label), sem?.label].filter(Boolean).join(" · ") || "—"],
    ["Schedule",    o.schedule || "—"],
    ["Room",        roomName(o, roomById) || "—"],
  ];

  const formSec = form ? allSections.find(x => String(x.id) === String(form.sectionId)) : null;
  const subjectOptions = formSec
    ? subjects.filter(x => String(x.gradeLevelId) === String(formSec.gradeLevelId))
    : subjects;

  return (
    <div className={s.overlay}>
      <div className={`${s.modal} ${s.assignedDetail}`}>
        <div className={s.modalHead}>
          <div className={s.modalHeadMain}>
            <div>
              <div className={s.modalTitle}>{subj?.title || "Class details"}</div>
              <div className={s.modalSub}>{sec?.name || "—"}{gl ? ` · ${gl.name}` : ""}</div>
            </div>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!editing ? (
          <dl className={s.assignedFields}>
            {fields.map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
        ) : (
          <div className={s.assignedEditForm}>
            <label className={s.filterField}>
              <span>Teacher</span>
              <select value={form.teacherStaffId}
                      onChange={e => setForm(f => ({ ...f, teacherStaffId: e.target.value }))}>
                <option value="">— Select a teacher —</option>
                {teachers.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className={s.filterField}>
              <span>Section</span>
              <select value={form.sectionId} onChange={e => setSection(e.target.value)}>
                <option value="">— Section —</option>
                <SectionOptions sections={sections} gradeLevels={gradeLevels} />
              </select>
            </label>
            <label className={s.filterField}>
              <span>Subject</span>
              <select value={form.subjectId}
                      onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}>
                <option value="">— Subject —</option>
                {subjectOptions.map(x => {
                  const k = `${x.id}|${form.sectionId}`;
                  const taken = k !== `${o.subjectId}|${o.sectionId}` ? takenBy.get(k) : null;
                  return (
                    <option key={x.id} value={x.id} disabled={!!taken}>
                      {x.code} · {x.title}{taken ? ` — taken by ${taken.teacherName}` : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className={s.filterField}>
              <span>Schedule</span>
              <SchedulePicker value={form.schedule}
                              onChange={v => setForm(f => ({ ...f, schedule: v }))} />
            </div>
            <div className={s.filterField}>
              <span>Room</span>
              <RoomPicker value={form.roomId}
                          onChange={roomId => setForm(f => ({ ...f, roomId }))}
                          placeholder="Pick a room…" />
            </div>
          </div>
        )}

        {err && <div className={s.error}>{err}</div>}

        <div className={s.modalFoot}>
          {!editing ? (
            <>
              <button type="button" className={s.viewRosterBtn}
                      onClick={() => setRoster(true)} disabled={busy || !sec}>
                View students
              </button>
              <span className={s.footSpacer} />
              <button className={s.btnDanger} onClick={remove} disabled={busy}>Delete</button>
              <button className={s.btnPrimary} onClick={startEdit} disabled={busy}>Edit</button>
            </>
          ) : (
            <>
              <button className={s.btnGhost} onClick={() => { setEditing(false); setErr(""); }} disabled={busy}>
                Cancel
              </button>
              <button className={s.btnPrimary} onClick={saveEdit} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <SectionRosterModal
        open={roster}
        section={sec ? { id: sec.id, name: sec.name } : null}
        gradeLabel={gl?.name || null}
        schoolYearId={sy?.id || null}
        semesterId={sem?.id || null}
        schoolYearLabel={sy?.label || null}
        semesterLabel={sem?.label || null}
        students={students}
        enrollments={enrollments}
        sections={allSections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        rooms={Object.values(roomById)}
        onClose={() => setRoster(false)}
      />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
   Subject / Section detail modal — read-only list of offerings. Edits
   still happen through Teacher mode (which owns the inline form), so this
   modal is intentionally simpler — just a drill-down to answer "who's
   teaching this subject?" or "what classes does this section have?"
   ═══════════════════════════════════════════════════════════════════ */
function GroupDetailModal({
  mode, entity, title, offerings,
  subjectById, sectionById, employeeById, roomById = {},
  activeSy, activeSem,
  students, enrollments,
  sections = [], gradeLevels = [], schoolYears = [], semesters = [],
  onClose, onAddMore, onManageRoster,
}) {
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const sub = `${activeSy?.label ? syLabel(activeSy.label) : "—"}`
            + `${activeSem?.label ? ` · ${activeSem.label}` : ""}`
            + ` · ${offerings.length} class${offerings.length === 1 ? "" : "es"}`;

  // Portaled to <body> for the same containing-block reason as the
  // sibling modals in this file.
  return createPortal(
    <div className={s.overlay}>
      <div className={`${s.modal} ${s.detailModal}`}>
        <div className={s.modalHead}>
          <div className={s.modalHeadMain}>
            <div className={s.modalAvatar} style={{ background: avatarColor(title) }}>
              {mode === "subject" ? (entity?.code || "?") : initials(title)}
            </div>
            <div>
              <div className={s.modalTitle}>{title}</div>
              <div className={s.modalSub}>{sub}</div>
            </div>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Roster panel: only meaningful when the drill-down represents a
            single section (mode === "section"). Subject / Grade modes
            span multiple sections so a single roster doesn't apply. */}
        {mode === "section" && entity?.id != null && (
          <div className={s.detailRosterWrap}>
            <SectionRoster
              sectionId={entity.id}
              schoolYearId={activeSy?.id ?? null}
              semesterId={activeSem?.id ?? null}
              students={students}
              enrollments={enrollments}
              sections={sections}
              gradeLevels={gradeLevels}
              schoolYears={schoolYears}
              semesters={semesters}
              onManage={() => onManageRoster && onManageRoster(entity.id)}
            />
          </div>
        )}

        {offerings.length === 0 ? (
          <div className={s.detailEmpty}>No classes yet.</div>
        ) : (
          <ul className={s.detailList}>
            {offerings.map(o => {
              const subj = subjectById[o.subjectId];
              const sec  = sectionById[o.sectionId];
              const tea  = o.teacherStaffId ? employeeById[o.teacherStaffId] : null;
              const raw = o.schedule || "";
              const idx = raw.lastIndexOf(" ");
              const days = idx === -1 ? raw : raw.slice(0, idx);
              const time = idx === -1 ? "" : raw.slice(idx + 1);

              // Lead with the field that varies within the current grouping:
              //   subject mode → teacher varies      (lead with teacher)
              //   section mode → subject varies      (lead with subject)
              //   grade mode   → section varies      (lead with section)
              let avatarText, headline, sub;
              if (mode === "subject") {
                avatarText = initials(tea?.name || "?");
                headline   = tea?.name || "— No teacher —";
                sub        = sec?.name || "—";
              } else if (mode === "grade") {
                avatarText = subj?.code || "?";
                headline   = sec?.name || "—";
                sub        = `${subj?.title || "—"} · ${tea?.name || "— No teacher —"}`;
              } else { // section
                avatarText = subj?.code || "?";
                headline   = subj?.title || "—";
                sub        = tea?.name || "— No teacher —";
              }

              return (
                <li key={o.id} className={s.detailRow}>
                  <div className={s.detailCode}>{avatarText}</div>
                  <div className={s.detailTitle}>
                    <div>{headline}</div>
                    <div className={s.detailMeta}>{sub}</div>
                  </div>
                  <div className={s.detailDays}>{days || "—"}</div>
                  <div className={s.detailTime}>{time || "—"}</div>
                  <div className={s.detailRoom}>{roomName(o, roomById) || "—"}</div>
                  <div className={s.detailActions}>
                    {!tea && <span className={s.pillWarn}>No teacher</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className={s.modalFoot}>
          {mode === "section" && entity?.id != null && (
            <Link
              to={`/academics/sections/${entity.id}`}
              className={s.btnGhost}
              onClick={onClose}>
              Open full page →
            </Link>
          )}
          <button className={s.btnPrimary} onClick={onAddMore}>+ Add more classes</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
